import React, { useState, useEffect, useMemo, memo } from 'react';

import {
  Stack,
  TextField,
  IconButton,
  FormControl,
  MenuItem,
  Paper,
  InputLabel,
  Select,
  Typography,
  Backdrop,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DisabledByDefaultRounded';
import { categoryMap } from 'src/utils/catmap';
import { CurrencyField } from 'src/sections/scenarios/CurrencyField';

export const PatchCategoryRow = memo(
  ({ placeholder, category, value: _value, onChange, onDelete, nameForAccount, disabled }) => {
    const value = useMemo(() => _value, [_value]);
    const [amount, setAmount] = useState(value?.amount !== null ? parseFloat(value?.amount) : null);
    const [type, setType] = useState(value?.type ?? 'onetime');
    const [growth, setGrowth] = useState(value?.growth ?? false);
    const [txMemo, setTxMemo] = useState(value?.memo ?? '');

    useEffect(
      () => onChange(category, { amount: Number(amount), type, growth, memo: txMemo }),
      [value, type, growth, txMemo, amount]
    );

    const isRecurring = type === 'recurring';
    const name =
      categoryMap(category ?? value?.category)?.detailed ?? nameForAccount(category)?.name;

    return (
      <Paper sx={{ p: '8px', alignItems: 'center', minWidth: 300 }}>
        <BlockUI open={disabled} onClose={() => {}}>
          <Stack spacing={0.5} sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography fontWeight="bold" variant="body2">
                {name}
              </Typography>
              <IconButton onClick={onDelete} size="small" color="error" disabled={disabled}>
                <DeleteIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1}>
              <FormControl fullWidth size="small" disabled={disabled}>
                <CurrencyField
                  size="small"
                  id="amount"
                  label="Amount"
                  placeholder={placeholder}
                  value={amount}
                  onChange={(e) => setAmount(parseFloat(e.target.value))}
                />
              </FormControl>

              <FormControl fullWidth size="small" sx={{ maxWidth: 125 }} disabled={disabled}>
                <InputLabel id="type-label">Type</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  value={type}
                  label="Type"
                  onChange={(e, v) => setType(e.target.value)}
                  size="small"
                >
                  <MenuItem value="onetime">One Time</MenuItem>
                  <MenuItem value="recurring">Recurring</MenuItem>
                </Select>
              </FormControl>
              {isRecurring && (
                <FormControl sx={{ minWidth: 100 }} size="small">
                  <InputLabel id="growth-label">Growth</InputLabel>
                  <Select
                    labelId="growth-label"
                    id="growth"
                    value={growth}
                    label="Growth"
                    onChange={(e, v) => setGrowth(e.target.value)}
                    size="small"
                  >
                    <MenuItem value={false}>None</MenuItem>
                    <MenuItem value="month">Monthly</MenuItem>
                    <MenuItem value="quarter">Quarterly</MenuItem>
                    <MenuItem value="year">Yearly</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
            {!disabled && (
              <TextField
                multiline
                label="Notes"
                placeholder="Add a memo to this budget adjustment"
                value={txMemo}
                onChange={(e) => setTxMemo(e.target.value)}
                fullWidth
                rows={1}
                size="small"
                variant="filled"
              />
            )}
          </Stack>
        </BlockUI>
      </Paper>
    );
  }
);

const BlockUI = ({ open, onClose, children }) => (
  <div style={{ position: 'relative' }}>
    <Backdrop
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        position: 'absolute',
        textAlign: 'center',
        m: -0.5,
        p: 2,
      }}
      open={open}
      onClick={() => onClose()}
    >
      <Typography variant="caption" fontWeight="semibold" color="text.secondary">
        This change is associated with the current scenario. You may edit in the scenarios panel.
      </Typography>
    </Backdrop>
    {children}
  </div>
);
